/**
 * Os modelos de texto das ocorrências, em Tailwind: o que era o
 * `ProfessorNovo/components/TemplatesOcorrenciasMantine` (já apagado; a creche do portal do
 * professor usa este), com os mesmos modelos e a mesma API (`onTemplateSelect`, `selectedTemplate`).
 *
 * Fechado é uma linha só («Modelos de ocorrência»); aberto mostra as categorias em fichas e
 * os modelos dessa categoria, cada um com «Ver» (o texto todo, com a data de hoje) e «Usar».
 */
import { useState } from "react";
import { Icon } from "@iconify/react";
import { ChevronDown } from "lucide-react";
import { Botao, Ficha, Selo } from "@/Logigate/ui";
import { cn } from "@/lib/utils";

export interface ModeloOcorrencia {
    id: string;
    nome: string;
    texto: string;
    campos: {
        prioridade: string;
        categoria: string;
    };
}

export const MODELOS_OCORRENCIAS: Record<string, ModeloOcorrencia[]> = {
    "Comportamento Inadequado": [
        {
            id: "comp_1",
            nome: "Comportamento Disruptivo",
            texto: "Prezados Pais/Encarregados de Educação,\n\nVenho por este meio informar sobre comportamento disruptivo observado durante a aula de hoje ({data}).\n\nSolicitamos a vossa colaboração para conversar com o(a) vosso(a) educando(a) sobre a importância do respeito e disciplina em sala de aula.",
            campos: { prioridade: "Alta", categoria: "Comportamento Inadequado" },
        },
        {
            id: "comp_2",
            nome: "Falta de Respeito",
            texto: "Caros Encarregados de Educação,\n\nInformo sobre situações de falta de respeito observadas durante o dia {data}.\n\nÉ importante reforçar valores de respeito e convivência harmoniosa.",
            campos: { prioridade: "Alta", categoria: "Comportamento Inadequado" },
        },
    ],
    "Excelente Desempenho": [
        {
            id: "desemp_1",
            nome: "Participação Destacada",
            texto: "Prezados Pais/Encarregados de Educação,\n\nTenho o prazer de informar sobre participação exemplar nas actividades do dia {data}.\n\nParabéns pelo excelente trabalho educativo.",
            campos: { prioridade: "Positiva", categoria: "Excelente Desempenho" },
        },
        {
            id: "desemp_2",
            nome: "Melhoria Significativa",
            texto: "Caros Encarregados de Educação,\n\nÉ com satisfação que comunico progressos significativos observados em {data}.\n\nContinuem o bom trabalho de apoio.",
            campos: { prioridade: "Positiva", categoria: "Excelente Desempenho" },
        },
    ],
    "Questões de Saúde": [
        {
            id: "saude_1",
            nome: "Mal-estar em Aula",
            texto: "Prezados Pais/Encarregados de Educação,\n\nInformo sobre sinais de mal-estar observados durante o período escolar de {data}.\n\nRecomendamos atenção ao estado de saúde do(a) educando(a).",
            campos: { prioridade: "Urgente", categoria: "Questões de Saúde" },
        },
        {
            id: "saude_2",
            nome: "Acidente Menor",
            texto: "Caros Encarregados de Educação,\n\nComunico sobre pequeno acidente ocorrido durante o dia {data}.\n\nOs primeiro socorros foram prestados na escola.",
            campos: { prioridade: "Urgente", categoria: "Questões de Saúde" },
        },
    ],
    "Falta de Material": [
        {
            id: "material_1",
            nome: "Material Escolar em Falta",
            texto: "Prezados Pais/Encarregados de Educação,\n\nInformo sobre material necessário em falta desde {data}.\n\nSolicitamos atenção para garantir o aproveitamento escolar.",
            campos: { prioridade: "Média", categoria: "Falta de Material" },
        },
    ],
    "Outros Assuntos": [
        {
            id: "outros_1",
            nome: "Comunicação Geral",
            texto: "Prezados Pais/Encarregados de Educação,\n\nVenho por este meio comunicar sobre situação referente ao dia {data}.\n\nAgradecemos a vossa atenção.",
            campos: { prioridade: "Normal", categoria: "Outros Assuntos" },
        },
    ],
};

const ICONES_CATEGORIA: Record<string, string> = {
    "Comportamento Inadequado": "mdi:alert-octagon-outline",
    "Excelente Desempenho": "mdi:star-outline",
    "Questões de Saúde": "mdi:medical-bag",
    "Falta de Material": "mdi:pencil-ruler",
    "Outros Assuntos": "mdi:message-text-outline",
};

function corDaPrioridade(prioridade: string): string {
    switch (prioridade) {
        case "Urgente":
            return "red";
        case "Alta":
            return "orange";
        case "Positiva":
            return "green";
        case "Média":
            return "blue";
        default:
            return "gray";
    }
}

/** O texto do modelo com a data de hoje no lugar de `{data}`. */
export function textoDoModelo(modelo: ModeloOcorrencia): string {
    return modelo.texto.replace(/{data}/g, new Date().toLocaleDateString("pt-PT"));
}

interface ModelosOcorrenciasProps {
    onTemplateSelect: (template: ModeloOcorrencia) => void;
    selectedTemplate: ModeloOcorrencia | null;
}

export default function ModelosOcorrencias({ onTemplateSelect, selectedTemplate }: ModelosOcorrenciasProps) {
    const categorias = Object.keys(MODELOS_OCORRENCIAS);
    const [aberto, setAberto] = useState(false);
    const [categoria, setCategoria] = useState(categorias[0]);
    const [aVer, setAVer] = useState<string | null>(null);

    const usar = (modelo: ModeloOcorrencia) => {
        onTemplateSelect(modelo);
        setAVer(null);
        setAberto(false);
    };

    return (
        <div className="rounded-xl border border-zinc-200 bg-zinc-50/60 dark:border-zinc-800 dark:bg-zinc-900/40">
            <button
                type="button"
                onClick={() => setAberto((a) => !a)}
                aria-expanded={aberto}
                className="flex min-h-14 w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-left transition-colors hover:bg-zinc-100/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-portal/40 dark:hover:bg-zinc-800/50"
            >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-portal-suave text-portal-texto dark:bg-portal-claro/10 dark:text-portal-claro">
                    <Icon icon="mdi:text-box-multiple-outline" className="size-5" />
                </span>
                <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-zinc-900 dark:text-zinc-100">Modelos de ocorrência</span>
                    <span className="block text-xs text-zinc-500 dark:text-zinc-400">Começa por um texto pronto e ajusta-o à situação.</span>
                </span>
                <ChevronDown className={cn("size-4 shrink-0 text-zinc-400 transition-transform duration-200", aberto && "rotate-180")} aria-hidden />
            </button>

            {aberto && (
                <div className="space-y-3 border-t border-zinc-200 px-3.5 py-3 animate-in fade-in-0 slide-in-from-top-1 dark:border-zinc-800" data-nao-suja>
                    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Categorias">
                        {categorias.map((c) => (
                            <Ficha
                                key={c}
                                activa={c === categoria}
                                onClick={() => {
                                    setCategoria(c);
                                    setAVer(null);
                                }}
                                icone={<Icon icon={ICONES_CATEGORIA[c] ?? "mdi:tag-outline"} />}
                                tamanho="md"
                            >
                                {c}
                            </Ficha>
                        ))}
                    </div>

                    <ul className="grid gap-2 sm:grid-cols-2">
                        {MODELOS_OCORRENCIAS[categoria].map((modelo) => {
                            const escolhido = selectedTemplate?.id === modelo.id;
                            const emVista = aVer === modelo.id;
                            return (
                                <li
                                    key={modelo.id}
                                    className={cn(
                                        "flex flex-col rounded-lg bg-white p-3 ring-1 transition-shadow dark:bg-zinc-900",
                                        escolhido ? "ring-2 ring-portal dark:ring-portal-claro" : "ring-zinc-950/10 dark:ring-white/10",
                                        emVista && "sm:col-span-2",
                                    )}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{modelo.nome}</p>
                                        <Selo cor={corDaPrioridade(modelo.campos.prioridade)} tamanho="xs">{modelo.campos.prioridade}</Selo>
                                    </div>
                                    <p className={cn("mt-1 flex-1 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400", emVista ? "whitespace-pre-line" : "line-clamp-2")}>
                                        {emVista ? textoDoModelo(modelo) : modelo.texto.replace(/\s+/g, " ")}
                                    </p>
                                    <div className="mt-2.5 flex gap-2">
                                        <Botao
                                            variante="fantasma"
                                            cor="gray"
                                            tamanho="sm"
                                            icone={<Icon icon={emVista ? "mdi:eye-off-outline" : "mdi:eye-outline"} className="size-4" />}
                                            onClick={() => setAVer(emVista ? null : modelo.id)}
                                            className="h-10 flex-1 sm:h-8 sm:flex-none"
                                        >
                                            {emVista ? "Esconder" : "Ver"}
                                        </Botao>
                                        <Botao
                                            variante="suave"
                                            tamanho="sm"
                                            icone={<Icon icon="mdi:check" className="size-4" />}
                                            onClick={() => usar(modelo)}
                                            className="h-10 flex-1 sm:h-8 sm:flex-none"
                                        >
                                            Usar
                                        </Botao>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}
